/**
 * Deliverable Slice
 * Redux Toolkit slice for deliverable state management
 */

import { createSlice, createAsyncThunk, PayloadAction, createSelector } from '@reduxjs/toolkit';
import type { RootState } from '../index';
import type {
  Deliverable,
  DeliverableStatus,
  ContentPlatform,
  CreateDeliverableRequest,
  UpdateDeliverableRequest,
  CompleteDeliverableRequest,
} from '@creatoros/shared';
import { deliverableService } from '../../services/deliverable.service';
import { asArray } from '../../services/api.unwrap';
import { ApiErrorState } from '../../types/error.types';
import { logger } from '../../utils/logger';

/**
 * Deliverable State Interface
 */
export interface DeliverableState {
  deliverables: Deliverable[];
  selectedDeliverable: Deliverable | null;
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  error: ApiErrorState | null;
  filters: {
    dealId?: string;
    status?: DeliverableStatus;
    platform?: ContentPlatform;
  };
}

/**
 * Initial State
 */
const initialState: DeliverableState = {
  deliverables: [],
  selectedDeliverable: null,
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  error: null,
  filters: {},
};

/**
 * Async Thunks
 */

/**
 * Fetch all deliverables (for dashboard)
 */
export const fetchAllDeliverables = createAsyncThunk<
  Deliverable[],
  void,
  { rejectValue: ApiErrorState }
>('deliverables/fetchAll', async (_, { rejectWithValue }) => {
  try {
    logger.debug('Fetching deliverables', { event: 'deliverables.fetch.started' });
    const response = await deliverableService.list();
    const items = asArray<Deliverable>(response.data);
    logger.debug('Fetched deliverables', {
      event: 'deliverables.fetch.succeeded',
      count: items.length,
    });
    return items;
  } catch (err: any) {
    logger.error('Failed to fetch deliverables', err, {
      event: 'deliverables.fetch.failed',
      code: err.code || err.response?.data?.errorType,
    });
    return rejectWithValue({
      message: err.message || err.response?.data?.error || 'Failed to fetch deliverables',
      code: err.code || err.response?.data?.errorType,
      details: err.details || err.response?.data?.details,
      timestamp: new Date().toISOString(),
    });
  }
});

/**
 * Fetch deliverables by deal ID
 */
export const fetchDeliverablesByDeal = createAsyncThunk<
  Deliverable[],
  string,
  { rejectValue: ApiErrorState }
>('deliverables/fetchByDeal', async (dealId, { rejectWithValue }) => {
  try {
    logger.debug('Fetching deliverables by deal', {
      event: 'deliverables.fetch_by_deal.started',
      dealId,
    });
    const response = await deliverableService.getByDeal(dealId);
    const items = asArray<Deliverable>(response.data);
    logger.debug('Fetched deliverables by deal', {
      event: 'deliverables.fetch_by_deal.succeeded',
      dealId,
      count: items.length,
    });
    return items;
  } catch (err: any) {
    logger.error('Failed to fetch deliverables by deal', err, {
      event: 'deliverables.fetch_by_deal.failed',
      dealId,
      code: err.code || err.response?.data?.errorType,
    });
    return rejectWithValue({
      message: err.message || err.response?.data?.error || 'Failed to fetch deliverables',
      code: err.code || err.response?.data?.errorType,
      details: err.details || err.response?.data?.details,
      timestamp: new Date().toISOString(),
    });
  }
});

/**
 * Fetch a single deliverable by ID
 */
export const fetchDeliverableById = createAsyncThunk<
  Deliverable,
  string,
  { rejectValue: ApiErrorState }
>('deliverables/fetchById', async (id, { rejectWithValue }) => {
  try {
    logger.debug('Fetching deliverable', {
      event: 'deliverables.fetch_by_id.started',
      deliverableId: id,
    });
    const item = await deliverableService.getById(id);
    logger.debug('Fetched deliverable', {
      event: 'deliverables.fetch_by_id.succeeded',
      deliverableId: id,
    });
    return item;
  } catch (err: any) {
    logger.error('Failed to fetch deliverable', err, {
      event: 'deliverables.fetch_by_id.failed',
      deliverableId: id,
      code: err.code || err.response?.data?.errorType,
    });
    return rejectWithValue({
      message: err.message || err.response?.data?.error || 'Failed to fetch deliverable',
      code: err.code || err.response?.data?.errorType,
      details: err.details || err.response?.data?.details,
      timestamp: new Date().toISOString(),
    });
  }
});

/**
 * Create a new deliverable
 */
export const createDeliverable = createAsyncThunk<
  Deliverable,
  CreateDeliverableRequest,
  { rejectValue: ApiErrorState }
>('deliverables/create', async (data, { rejectWithValue }) => {
  try {
    const item = await deliverableService.create(data);
    logger.info('Deliverable created', {
      event: 'deliverables.create.succeeded',
      deliverableId: item.id,
    });
    return item;
  } catch (err: any) {
    logger.error('Failed to create deliverable', err, {
      event: 'deliverables.create.failed',
      code: err.code || err.response?.data?.errorType,
    });
    return rejectWithValue({
      message: err.message || err.response?.data?.error || 'Failed to create deliverable',
      code: err.code || err.response?.data?.errorType,
      details: err.details || err.response?.data?.details,
      timestamp: new Date().toISOString(),
    });
  }
});

/**
 * Update an existing deliverable
 */
export const updateDeliverable = createAsyncThunk<
  Deliverable,
  { id: string; data: UpdateDeliverableRequest },
  { rejectValue: ApiErrorState }
>('deliverables/update', async ({ id, data }, { rejectWithValue }) => {
  try {
    const item = await deliverableService.update(id, data);
    logger.info('Deliverable updated', {
      event: 'deliverables.update.succeeded',
      deliverableId: id,
    });
    return item;
  } catch (err: any) {
    logger.error('Failed to update deliverable', err, {
      event: 'deliverables.update.failed',
      deliverableId: id,
      code: err.code || err.response?.data?.errorType,
    });
    return rejectWithValue({
      message: err.message || err.response?.data?.error || 'Failed to update deliverable',
      code: err.code || err.response?.data?.errorType,
      details: err.details || err.response?.data?.details,
      timestamp: new Date().toISOString(),
    });
  }
});

/**
 * Mark deliverable as complete
 */
export const completeDeliverable = createAsyncThunk<
  Deliverable,
  { id: string; data: CompleteDeliverableRequest },
  { rejectValue: ApiErrorState }
>('deliverables/complete', async ({ id, data }, { rejectWithValue }) => {
  try {
    const item = await deliverableService.complete(id, data);
    logger.info('Deliverable completed', {
      event: 'deliverables.complete.succeeded',
      deliverableId: id,
    });
    return item;
  } catch (err: any) {
    logger.error('Failed to complete deliverable', err, {
      event: 'deliverables.complete.failed',
      deliverableId: id,
      code: err.code || err.response?.data?.errorType,
    });
    return rejectWithValue({
      message: err.message || err.response?.data?.error || 'Failed to complete deliverable',
      code: err.code || err.response?.data?.errorType,
      details: err.details || err.response?.data?.details,
      timestamp: new Date().toISOString(),
    });
  }
});

/**
 * Delete a deliverable
 */
export const deleteDeliverable = createAsyncThunk<string, string, { rejectValue: ApiErrorState }>(
  'deliverables/delete',
  async (id, { rejectWithValue }) => {
    try {
      await deliverableService.delete(id);
      logger.info('Deliverable deleted', {
        event: 'deliverables.delete.succeeded',
        deliverableId: id,
      });
      return id;
    } catch (err: any) {
      logger.error('Failed to delete deliverable', err, {
        event: 'deliverables.delete.failed',
        deliverableId: id,
        code: err.code || err.response?.data?.errorType,
      });
      return rejectWithValue({
        message: err.message || err.response?.data?.error || 'Failed to delete deliverable',
        code: err.code || err.response?.data?.errorType,
        details: err.details || err.response?.data?.details,
        timestamp: new Date().toISOString(),
      });
    }
  }
);

/**
 * Deliverable Slice
 */
const deliverableSlice = createSlice({
  name: 'deliverables',
  initialState,
  reducers: {
    /**
     * Set selected deliverable
     */
    setSelectedDeliverable: (state, action: PayloadAction<Deliverable | null>) => {
      state.selectedDeliverable = action.payload;
    },

    /**
     * Clear selected deliverable
     */
    clearSelectedDeliverable: (state) => {
      state.selectedDeliverable = null;
    },

    /**
     * Set filters
     */
    setDeliverableFilters: (
      state,
      action: PayloadAction<{
        dealId?: string;
        status?: DeliverableStatus;
        platform?: ContentPlatform;
      }>
    ) => {
      state.filters = { ...state.filters, ...action.payload };
    },

    /**
     * Clear filters
     */
    clearDeliverableFilters: (state) => {
      state.filters = {};
    },

    /**
     * Clear error
     */
    clearError: (state) => {
      state.error = null;
    },

    /**
     * Reset state
     */
    resetDeliverableState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Fetch all deliverables (for dashboard)
      .addCase(fetchAllDeliverables.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllDeliverables.fulfilled, (state, action) => {
        state.isLoading = false;
        state.deliverables = asArray(action.payload);
      })
      .addCase(fetchAllDeliverables.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? { message: 'Failed to fetch deliverables' };
      })

      // Fetch deliverables by deal
      .addCase(fetchDeliverablesByDeal.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDeliverablesByDeal.fulfilled, (state, action) => {
        state.isLoading = false;
        state.deliverables = asArray(action.payload);
      })
      .addCase(fetchDeliverablesByDeal.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? { message: 'Failed to fetch deliverables' };
      })

      // Fetch deliverable by ID
      .addCase(fetchDeliverableById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDeliverableById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedDeliverable = action.payload;
        // Update in list if exists
        const index = state.deliverables.findIndex((d) => d.id === action.payload.id);
        if (index !== -1) {
          state.deliverables[index] = action.payload;
        }
      })
      .addCase(fetchDeliverableById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? { message: 'Failed to fetch deliverable' };
      })

      // Create deliverable
      .addCase(createDeliverable.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createDeliverable.fulfilled, (state, action) => {
        state.isCreating = false;
        state.deliverables.push(action.payload);
      })
      .addCase(createDeliverable.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload ?? { message: 'Failed to create deliverable' };
      })

      // Update deliverable
      .addCase(updateDeliverable.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateDeliverable.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.deliverables.findIndex((d) => d.id === action.payload.id);
        if (index !== -1) {
          state.deliverables[index] = action.payload;
        }
        if (state.selectedDeliverable?.id === action.payload.id) {
          state.selectedDeliverable = action.payload;
        }
      })
      .addCase(updateDeliverable.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload ?? { message: 'Failed to update deliverable' };
      })

      // Complete deliverable
      .addCase(completeDeliverable.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(completeDeliverable.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.deliverables.findIndex((d) => d.id === action.payload.id);
        if (index !== -1) {
          state.deliverables[index] = action.payload;
        }
        if (state.selectedDeliverable?.id === action.payload.id) {
          state.selectedDeliverable = action.payload;
        }
      })
      .addCase(completeDeliverable.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload ?? { message: 'Failed to complete deliverable' };
      })

      // Delete deliverable
      .addCase(deleteDeliverable.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteDeliverable.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.deliverables = state.deliverables.filter((d) => d.id !== action.payload);
        if (state.selectedDeliverable?.id === action.payload) {
          state.selectedDeliverable = null;
        }
      })
      .addCase(deleteDeliverable.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload ?? { message: 'Failed to delete deliverable' };
      });
  },
});

/**
 * Selectors
 */

// Base selectors
export const selectDeliverables = (state: RootState) =>
  asArray<Deliverable>(state.deliverables.deliverables);
export const selectSelectedDeliverable = (state: RootState) =>
  state.deliverables.selectedDeliverable;
export const selectDeliverableError = (state: RootState) => state.deliverables.error;
export const selectDeliverableIsLoading = (state: RootState) => state.deliverables.isLoading;
export const selectDeliverableIsCreating = (state: RootState) => state.deliverables.isCreating;
export const selectDeliverableIsUpdating = (state: RootState) => state.deliverables.isUpdating;
export const selectDeliverableIsDeleting = (state: RootState) => state.deliverables.isDeleting;
export const selectDeliverableFilters = (state: RootState) => state.deliverables.filters;

// Derived selectors
export const selectDeliverableById = (state: RootState, id: string) =>
  selectDeliverables(state).find((deliverable) => deliverable.id === id);

export const selectDeliverablesByDeal = (state: RootState, dealId: string) =>
  selectDeliverables(state).filter((deliverable) => deliverable.dealId === dealId);

export const selectDeliverablesByStatus = (state: RootState, status: DeliverableStatus) =>
  selectDeliverables(state).filter((deliverable) => deliverable.status === status);

export const selectDeliverablesByPlatform = (state: RootState, platform: ContentPlatform) =>
  selectDeliverables(state).filter((deliverable) => deliverable.platform === platform);

export const selectCompletedDeliverables = (state: RootState) =>
  selectDeliverables(state).filter((deliverable) => deliverable.isCompleted);

export const selectPendingDeliverables = (state: RootState) =>
  selectDeliverables(state).filter(
    (deliverable) => !deliverable.isCompleted && deliverable.status !== 'cancelled'
  );

export const selectOverdueDeliverables = (state: RootState) => {
  const now = new Date();
  return selectDeliverables(state).filter(
    (deliverable) =>
      !deliverable.isCompleted && deliverable.dueDate && new Date(deliverable.dueDate) < now
  );
};

export const selectFilteredDeliverables = (state: RootState) => {
  let filtered = selectDeliverables(state);
  const { dealId, status, platform } = state.deliverables.filters;

  if (dealId) {
    filtered = filtered.filter((deliverable) => deliverable.dealId === dealId);
  }
  if (status) {
    filtered = filtered.filter((deliverable) => deliverable.status === status);
  }
  if (platform) {
    filtered = filtered.filter((deliverable) => deliverable.platform === platform);
  }

  return filtered;
};

export const selectDeliverablesCount = (state: RootState) => selectDeliverables(state).length;

export const selectCompletionRate = (state: RootState) => {
  const deliverables = selectDeliverables(state);
  const total = deliverables.length;
  if (total === 0) return 0;
  const completed = deliverables.filter((d) => d.isCompleted).length;
  return (completed / total) * 100;
};

/**
 * Selector: Deliverables grouped by platform for chart widget
 * Memoized to prevent new array references on every call
 */
export const selectDeliverablesByPlatformGrouped = createSelector(
  [selectDeliverables],
  (deliverables) => {
    const platformCounts: Record<string, number> = {};

    deliverables.forEach((d) => {
      const platform = d.platform || 'other';
      platformCounts[platform] = (platformCounts[platform] || 0) + 1;
    });

    return Object.entries(platformCounts).map(([platform, count]) => ({
      platform,
      count,
    }));
  }
);

/**
 * Selector: Deliverables grouped by status for chart widget
 * Memoized to prevent new array references on every call
 */
export const selectDeliverablesByStatusGrouped = createSelector(
  [selectDeliverables],
  (deliverables) => {
    const statusCounts: Record<string, number> = {};

    deliverables.forEach((d) => {
      const status = d.status || 'pending';
      statusCounts[status] = (statusCounts[status] || 0) + 1;
    });

    return Object.entries(statusCounts).map(([status, count]) => ({
      status,
      count,
    }));
  }
);

/**
 * Selector: Count of overdue deliverables
 */
export const selectOverdueDeliverablesCount = (state: RootState) => {
  const now = new Date();
  return selectDeliverables(state).filter(
    (d) => !d.isCompleted && d.dueDate && new Date(d.dueDate) < now
  ).length;
};

/**
 * Selector: Count of pending deliverables
 */
export const selectPendingDeliverablesCount = (state: RootState) => {
  return selectDeliverables(state).filter((d) => !d.isCompleted && d.status !== 'cancelled').length;
};

/**
 * Export actions and reducer
 */
export const {
  setSelectedDeliverable,
  clearSelectedDeliverable,
  setDeliverableFilters,
  clearDeliverableFilters,
  clearError,
  resetDeliverableState,
} = deliverableSlice.actions;

export default deliverableSlice.reducer;
